/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { adminHasPermission, cleanUpAdminTransactions } from '../../../utils/helpers';
import useCustomQuery from '../../../network/useCustomQuery';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import { GET_ALL_TRANSACTIONS } from '../../../graphql/admin_queries';
import AdminTable from '../../../components/AdminTable';
import DashboardHeader from '../../../components/DashboardHeader';
import AdminLinkTabs from '../../../components/AdminLinkTabs';
import useToast from '../../../customHooks/useToast';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import EmptyRecord from '../../../components/EmptyRecord';
import useTableColumn from '../../../customHooks/useTableColumn';
import generatePDF from '../../../utils/generatePdf';
import generateCSV from '../../../utils/generateCSV';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import { useGlobalState } from '../../../contexts';
import CustomerTabs from '../../AdminCustomers/CustomerTabs';
import RolesAndPermissionGuard from '../../../components/RolesAndPermissionGuard';
import PermissionDenied from '../../PermissionDenied';
import { AdminRouteName } from '../../CONSTANTS';

const limit = 14;
interface IMerchantTableRecord{
defaultUrl?:string;
toggleStorageId?:string;
tabStatus?:string;
hasNameAndImage?:boolean;
recordPermission:string;
payload?: any;
userId?: string;
noSubTab?: boolean
}
const TransactionTableRecords = ({
  defaultUrl, toggleStorageId, tabStatus, hasNameAndImage, recordPermission, payload, userId, noSubTab,
}:IMerchantTableRecord) => {
  const { profile } = useGlobalState();
  const adminPermissions = profile?.role?.permissions || [];
  const Toast = useToast();
  const [getTransactions, transactionLoading, transaction] = useCustomQuery({
    query: GET_ALL_TRANSACTIONS,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'dateTime',
    'amount',
    'fees',
    'description',
    'paymentMethod',
    'status',
    'message',
  ];
  const columnLabelFormat = {
    name: 'Customer',
    dateTime: 'Date',
    amount: 'Total Amount',
    fees: 'Processing Fee',
    description: 'Description',
    paymentMethod: 'Payment Method',
    message: 'Reason',
  };
  const { getColumns, applyChanges, selectedColumns } = useTableColumn(allColumns, toggleStorageId);

  const [trxRec, setrxRec] = useState(null);
  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`${defaultUrl}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    push(`${defaultUrl}?page=1&search=${e.target.value}`);
  };

  const TabsRaw = [
    {
      id: 1,
      name: 'All',
      link: AdminRouteName.transactionRoute,
      permission: 'can-view-all-transaction-tab',
      count: trxRec?.count || 0,
    },
    {
      id: 2,
      name: 'Pending',
      link: AdminRouteName.pendingTransactionRoute,
      permission: 'can-view-pending-transaction',
      count: trxRec?.count || 0,
    },
    {
      id: 3,
      name: 'Successful',
      link: AdminRouteName.successfulTransactionRoute,
      permission: 'can-view-successful-transaction',
      count: trxRec?.count || 0,
    },
    {
      id: 4,
      name: 'Failed',
      link: AdminRouteName.failedTransactionRoute,
      permission: 'can-view-failed-transaction',
      count: trxRec?.count || 0,
    },
  ];

  const Tabs = TabsRaw?.map((tab) => ({
    ...tab,
    disabled: !adminHasPermission(adminPermissions, tab.permission),
  }));

  useEffect(() => {
    if (page) {
      const result = getFilterQueries();
      getTransactions({
        variables: {
          offset: (page - 1) * limit,
          limit,
          ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          userId,
          filters: {
            from: result.from,
            to: result.to,
            ...(tabStatus ? {} : { status: result?.status }),
            minAmount: result.min ? Number(result.min) : 0,
            maxAmount: result.max ? Number(result.max) : 0,
          },
        },
      });
    }
  }, [page, search]);

  const onFilterSubmit = (result) => {
    getTransactions({
      variables: {
        offset: 0,
        limit,
        ...(tabStatus ? { statusFilter: tabStatus } : {}),
        search,
        userId,
        filters: {
          from: result?.from,
          to: result?.to,
          status: result?.status,
          minAmount: result?.min ? Number(result?.min) : 0,
          maxAmount: result?.max ? Number(result?.max) : 0,
        },
      },
    });
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: tabStatus ? ['dateRange', 'amountRange'] : ['dateRange', 'status', 'amountRange'],
    defaultUrl,
    cb: onFilterSubmit,
    Toast,
    customFilterInformation: {
      dateRange: {
        label: 'Date',
      },
      amountRange: {
        label: 'Total Amount',
      },
      status: {
        label: 'Status',
        options: [
          {
            label: 'Successful',
            value: 'success',
          },
          {
            label: 'Pending',
            value: 'pending',
          },
          {
            label: 'Failed',
            value: 'failed',
          },
        ],
      },
    },
  });

  useEffect(() => {
    if (transaction?.getAllTransactionsAdminCa?.transactions) {
      setrxRec(transaction?.getAllTransactionsAdminCa);
    }
  }, [transaction]);

  const [getExportRecord] = useCustomQuery({
    query: GET_ALL_TRANSACTIONS,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          ...(tabStatus ? { statusFilter: tabStatus } : {}),
          search,
          userId,
          filters: {
            from: result?.from,
            to: result?.to,
            ...(tabStatus ? {} : { status: result?.status }),
            minAmount: result?.min ? Number(result.min) : 0,
            maxAmount: result?.max ? Number(result.max) : 0,
          },
        },
      });
      const transactions = output?.data?.getAllTransactionsAdminCa?.transactions;
      if (!transactions?.length) {
        Toast.error('No transactions to export');
        return;
      }
      const rec = cleanUpAdminTransactions(transactions);
      const func = type === 'pdf' ? generatePDF : generateCSV;
      const selectedHeaders = [
        'S/N',
        ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
      ];

      func({
        payload: rec,
        title: 'TRANSACTIONS',
        selectedHeaders,
        formatHeaders: columnLabelFormat,
        cb: () => Toast.success('Exported successfully'),
      });
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  return (
    <div>
      <DashboardHeader
        title="Transactions"
        smallSubTitle={`(${trxRec?.count || 0})`}
        searchPlaceholder="Search by customer"
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={transactionLoading}
        filterObj={filterObj}
        columnObject={{
          allColumns,
          formatLabels: columnLabelFormat,
          applyChanges,
          selectedColumns,
        }}
        exportObj={{
          handleExport,
        }}
        disableExport={!adminHasPermission(adminPermissions, 'can-export-transaction-records')}
        disableSearch={!adminHasPermission(adminPermissions, 'can-search-transaction-records')}
        disbleFilter={!adminHasPermission(adminPermissions, 'can-filter-transaction-records')}
        payload={payload}
      >
        {!noSubTab && (

        <AdminLinkTabs>
          <CustomerTabs Tabs={Tabs} />
        </AdminLinkTabs>
        )}
      </DashboardHeader>

      <RolesAndPermissionGuard permissionName={recordPermission} hasErrorTemplate={<PermissionDenied height="60vh" />}>
        <div className="transaction-table-details-cover">
          {trxRec?.count ? (
            <AdminTable
              refreshing={transactionLoading}
              page={page}
              setPage={setPage}
              pageSize={limit}
              minHeight="calc(100vh - 280px)"
              count={trxRec?.count}
              contents={cleanUpAdminTransactions(trxRec?.transactions)}
              reference="id"
              hideDominantImage
              clickable
              hasNameAndImage={hasNameAndImage}
              handleClick={(data) => push(`/transactions/transactions-details/${data?.id}`)}
              allowKeyArr={getColumns(true)}
              mobileAllowKeyArr={
                [
                  'status',
                  ['merchant', 'txRef'],
                  ['email', 'customer'],
                ]
              }
              formatLabels={columnLabelFormat}
            />
          ) : (
            <EmptyRecord
              height="calc(100vh - 240px)"
              title={transactionLoading ? 'Fetching transactions...' : 'No transactions yet'}
              main="All transactions from customers on your Payfi will appear here"
              // footer="Receive Payments"
              loading={transactionLoading}
              Icon={EmptyMerchantSettlement}
            />
          )}

        </div>
      </RolesAndPermissionGuard>

    </div>
  );
};
export default TransactionTableRecords;
